import styled, { css } from "styled-components";

import {
  md3ButtonRadius,
  md3PressTransition,
  md3StateLayer,
} from "../../styles/md3Components";
import {
  resolveMd3ButtonPalette,
  sizeConfig,
  type Md3ButtonColor,
  type Md3ButtonSize,
  type Md3ButtonVariant,
} from "./palette";

type StyledProps = {
  $variant: Md3ButtonVariant;
  $color: Md3ButtonColor;
  $size: Md3ButtonSize;
  $fullWidth?: boolean;
};

export const StyledMd3Button = styled.button<StyledProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  transition: ${({ theme }) => md3PressTransition(theme)};
  min-height: ${({ $size }) => sizeConfig[$size].minHeight}px;
  padding: 0 ${({ $size }) => sizeConfig[$size].paddingX}px;
  font-size: ${({ $size }) => sizeConfig[$size].fontSize}px;
  border-radius: ${({ theme, $variant }) =>
    $variant === "text"
      ? `${theme.md3.shape.medium}px`
      : `${md3ButtonRadius(theme)}px`};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  overflow: hidden;

  ${({ theme, $variant, $color }) => {
    const palette = resolveMd3ButtonPalette(theme, $variant, $color);
    return css`
      background: ${palette.background};
      color: ${palette.label};
      border: ${$variant === "outlined"
        ? `1px solid ${palette.border}`
        : "none"};

      &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: ${md3StateLayer(
          palette.stateLayer,
          theme.md3.stateLayer.hover
        )};
        opacity: 0;
        transition: opacity ${theme.md3.motion.duration.short3}ms
          ${theme.md3.motion.easing.standard};
        pointer-events: none;
      }

      &:hover:not(:disabled)::before {
        opacity: 1;
      }

      &:active:not(:disabled) {
        transform: scale(${theme.md3.motion.scale.pressedExpressive});
      }

      &:focus-visible {
        outline: 2px solid ${theme.colors.focus};
        outline-offset: 2px;
      }

      &:disabled {
        opacity: ${({ theme }) => theme.md3.stateLayer.focus + 0.33};
        cursor: not-allowed;
      }
    `;
  }}
`;

export const LoadingRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
